import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Box, Checkbox, Chip, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import { DatePicker, SelectMenu } from '@livable/saas-react-components';
import { addMonths, format, isBefore, startOfDay } from 'date-fns';
import useStyles from './styles';
import { COUPON_APPLIES_TO, COUPON_DISCOUNT_TYPE, COUPON_DISCOUNT_TYPE_OPTIONS, COUPON_DURATION_OPTIONS, COUPON_EXPIRATION_OPTIONS, COUPON_MAX_REDEMPTION_OPTIONS, COUPON_SCOPE, DURATION_FOREVER, IS_REDEMPTION_LIMIT_VISIBLE, EXPIRATION_CUSTOM, EXPIRATION_NONE, MAX_REDEMPTIONS_CUSTOM, MAX_REDEMPTIONS_UNLIMITED, formatCouponProductLabel, getCouponProductIds, isExpirationDateAllowed, parseCouponDate } from '../couponsModel';
const DATE_FORMAT = 'MM/dd/yy';
const EXPIRES_FORMAT = 'M/d/yyyy';
const SCOPE_OPTIONS = [{
  value: COUPON_SCOPE.ACCOUNT,
  label: 'Account'
}, {
  value: COUPON_SCOPE.PROPERTY,
  label: 'Property'
}, {
  value: COUPON_SCOPE.BOTH,
  label: 'Both'
}];
const APPLIES_TO_OPTIONS = [{
  value: COUPON_APPLIES_TO.INVOICE,
  label: 'Entire invoice'
}, {
  value: COUPON_APPLIES_TO.PRODUCTS,
  label: 'Specific products'
}];

/** Column widths of the backend's Coupons table (Coupon.NameMaxLength / CodeMaxLength). */
const NAME_MAX_LENGTH = 255;
const CODE_MAX_LENGTH = 50;

/** The backend stores codes uppercased and matches them case-insensitively. */
function sanitizeCode(value) {
  return String(value !== null && value !== void 0 ? value : '').toUpperCase().replace(/[^A-Z0-9-_]/g, '-');
}
function sanitizeIntegerInput(value) {
  return String(value !== null && value !== void 0 ? value : '').replace(/\D/g, '');
}
function sanitizeDecimalInput(value) {
  let s = String(value !== null && value !== void 0 ? value : '').replace(/[^\d.]/g, '');
  const dotIndex = s.indexOf('.');
  if (dotIndex !== -1) {
    s = s.slice(0, dotIndex + 1) + s.slice(dotIndex + 1).replace(/\./g, '').slice(0, 2);
  }
  return s;
}
const PRESET_MAX_REDEMPTIONS = COUPON_MAX_REDEMPTION_OPTIONS.map(o => o.value).filter(v => typeof v === 'number');
const emptyForm = {
  name: '',
  code: '',
  discountType: COUPON_DISCOUNT_TYPE.PERCENT,
  discountValue: '',
  duration: 1,
  scope: COUPON_SCOPE.ACCOUNT,
  expirationOption: EXPIRATION_NONE,
  customExpirationDate: null,
  maxRedemptionsOption: MAX_REDEMPTIONS_UNLIMITED,
  customMaxRedemptions: '',
  appliesTo: COUPON_APPLIES_TO.INVOICE,
  appliesToProductIds: []
};
export const CouponModalContent = /*#__PURE__*/forwardRef(function CouponModalContent(_ref, ref) {
  var _coupon$appliesToProd2;
  let isOpen = _ref.isOpen,
    isEdit = _ref.isEdit,
    coupon = _ref.coupon,
    existingCodes = _ref.existingCodes,
    products = _ref.products;
  const classes = useStyles();
  const _useState = useState(emptyForm),
    _useState2 = _slicedToArray(_useState, 2),
    form = _useState2[0],
    setForm = _useState2[1];
  const _useState3 = useState({}),
    _useState4 = _slicedToArray(_useState3, 2),
    errors = _useState4[0],
    setErrors = _useState4[1];
  useEffect(() => {
    var _coupon$name, _coupon$code, _coupon$discountType, _coupon$scope, _coupon$appliesToProd;
    if (!isOpen) return;
    setErrors({});
    if (!isEdit || !coupon) {
      setForm(emptyForm);
      return;
    }
    const max = coupon.maxRedemptions;
    setForm({
      name: (_coupon$name = coupon.name) !== null && _coupon$name !== void 0 ? _coupon$name : '',
      code: (_coupon$code = coupon.code) !== null && _coupon$code !== void 0 ? _coupon$code : '',
      discountType: (_coupon$discountType = coupon.discountType) !== null && _coupon$discountType !== void 0 ? _coupon$discountType : COUPON_DISCOUNT_TYPE.PERCENT,
      discountValue: coupon.discountValue != null ? String(coupon.discountValue) : '',
      duration: coupon.durationMonths == null ? DURATION_FOREVER : coupon.durationMonths,
      scope: (_coupon$scope = coupon.scope) !== null && _coupon$scope !== void 0 ? _coupon$scope : COUPON_SCOPE.ACCOUNT,
      // A stored date cannot be mapped back onto a relative period, so it always edits as custom.
      expirationOption: coupon.expirationDate ? EXPIRATION_CUSTOM : EXPIRATION_NONE,
      customExpirationDate: parseCouponDate(coupon.expirationDate),
      maxRedemptionsOption: max == null ? MAX_REDEMPTIONS_UNLIMITED : PRESET_MAX_REDEMPTIONS.includes(max) ? max : MAX_REDEMPTIONS_CUSTOM,
      customMaxRedemptions: max != null && !PRESET_MAX_REDEMPTIONS.includes(max) ? String(max) : '',
      appliesTo: (_coupon$appliesToProd = coupon.appliesToProducts) !== null && _coupon$appliesToProd !== void 0 && _coupon$appliesToProd.length ? COUPON_APPLIES_TO.PRODUCTS : COUPON_APPLIES_TO.INVOICE,
      appliesToProductIds: getCouponProductIds(coupon)
    });
  }, [isOpen, isEdit, coupon]);
  const setField = (field, value) => {
    setForm(prev => _objectSpread(_objectSpread({}, prev), {}, {
      [field]: value
    }));
    setErrors(prev => prev[field] ? _objectSpread(_objectSpread({}, prev), {}, {
      [field]: undefined
    }) : prev);
  };

  /**
   * A deleted product stays on the coupon but is no longer in the catalog. Its row is rendered from
   * the coupon itself, so saving cannot silently drop the restriction.
   */
  const catalogProducts = (products !== null && products !== void 0 ? products : []).filter(product => product === null || product === void 0 ? void 0 : product.id);
  const catalogIds = new Set(catalogProducts.map(product => product.id));
  const orphanProducts = ((_coupon$appliesToProd2 = coupon === null || coupon === void 0 ? void 0 : coupon.appliesToProducts) !== null && _coupon$appliesToProd2 !== void 0 ? _coupon$appliesToProd2 : []).filter(product => (product === null || product === void 0 ? void 0 : product.id) && !catalogIds.has(product.id)).map(product => _objectSpread(_objectSpread({}, product), {}, {
    isDeleted: true
  }));
  const productRows = [...catalogProducts, ...orphanProducts];
  const isPercent = form.discountType === COUPON_DISCOUNT_TYPE.PERCENT;
  const isProductScoped = form.appliesTo === COUPON_APPLIES_TO.PRODUCTS;
  const isCustomExpiration = form.expirationOption === EXPIRATION_CUSTOM;
  const isCustomMaxRedemptions = form.maxRedemptionsOption === MAX_REDEMPTIONS_CUSTOM;

  // The expiration date is the last day the code can be redeemed, so today still counts.
  const today = startOfDay(new Date());
  const storedExpiration = isEdit ? parseCouponDate(coupon === null || coupon === void 0 ? void 0 : coupon.expirationDate) : null;
  // An already-lapsed coupon keeps its own date as the floor: the server re-checks the expiry only
  // when it changes, so Ops can still fix the name or scope of a coupon that has run out.
  const earliestExpiration = storedExpiration && isBefore(storedExpiration, today) ? startOfDay(storedExpiration) : today;
  const resolveExpirationDate = () => {
    var _form$customExpiratio;
    if (form.expirationOption === EXPIRATION_NONE) return null;
    if (isCustomExpiration) return (_form$customExpiratio = form.customExpirationDate) !== null && _form$customExpiratio !== void 0 ? _form$customExpiratio : null;
    return addMonths(new Date(), Number(form.expirationOption));
  };
  const resolvedExpiration = resolveExpirationDate();
  const resolveMaxRedemptions = () => {
    if (form.maxRedemptionsOption === MAX_REDEMPTIONS_UNLIMITED) return null;
    if (isCustomMaxRedemptions) {
      const parsed = parseInt(form.customMaxRedemptions, 10);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return Number(form.maxRedemptionsOption);
  };
  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Coupon name is required';
    const code = form.code.trim();
    if (!code) {
      next.code = 'Code is required';
    } else if ((existingCodes !== null && existingCodes !== void 0 ? existingCodes : []).includes(code.toUpperCase())) {
      next.code = 'This code is already used by another coupon';
    }
    const value = parseFloat(form.discountValue);
    if (!form.discountValue || Number.isNaN(value) || value <= 0) {
      next.discountValue = 'Enter a discount greater than 0';
    } else if (isPercent && value > 100) {
      next.discountValue = 'Percentage cannot exceed 100';
    }
    if (isCustomExpiration && !form.customExpirationDate) {
      next.expirationOption = 'Pick an expiration date';
    } else if (isCustomExpiration && !isExpirationDateAllowed(form.customExpirationDate, storedExpiration, today)) {
      next.expirationOption = 'The expiration date cannot be in the past';
    }
    if (isCustomMaxRedemptions && !(parseInt(form.customMaxRedemptions, 10) > 0)) {
      next.maxRedemptionsOption = 'Enter a redemption limit greater than 0';
    }
    if (isProductScoped && form.appliesToProductIds.length === 0) {
      next.appliesToProductIds = 'Select at least one product';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const getPayload = () => ({
    name: form.name.trim(),
    code: form.code.trim(),
    discountType: form.discountType,
    discountValue: parseFloat(form.discountValue),
    durationMonths: form.duration === DURATION_FOREVER ? null : Number(form.duration),
    scope: form.scope,
    expirationDate: resolvedExpiration ? format(resolvedExpiration, 'yyyy-MM-dd') : null,
    maxRedemptions: resolveMaxRedemptions(),
    appliesToProductIds: isProductScoped ? form.appliesToProductIds : []
  });
  useImperativeHandle(ref, () => ({
    validate,
    getPayload
  }));
  const createProductToggleHandler = productId => () => {
    setForm(prev => _objectSpread(_objectSpread({}, prev), {}, {
      appliesToProductIds: prev.appliesToProductIds.includes(productId) ? prev.appliesToProductIds.filter(id => id !== productId) : [...prev.appliesToProductIds, productId]
    }));
    setErrors(prev => prev.appliesToProductIds ? _objectSpread(_objectSpread({}, prev), {}, {
      appliesToProductIds: undefined
    }) : prev);
  };
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.content
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.field
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    className: classes.fieldLabel
  }, "Coupon name"), /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    placeholder: "Trade Show Spring 2026",
    value: form.name,
    inputProps: {
      maxLength: NAME_MAX_LENGTH
    },
    onChange: e => setField('name', e.target.value),
    error: !!errors.name,
    helperText: errors.name
  })), /*#__PURE__*/React.createElement(Box, {
    className: classes.field
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    className: classes.fieldLabel
  }, "Coupon code"), /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    className: classes.codeInput,
    placeholder: "TRADE-SHOW-SPRING-2026",
    value: form.code,
    inputProps: {
      maxLength: CODE_MAX_LENGTH
    },
    onChange: e => setField('code', sanitizeCode(e.target.value)),
    error: !!errors.code,
    helperText: errors.code || 'Use a short, clear code that is easy to type and recognize. Uppercase letters, numbers and dashes only, with no spaces.'
  })), /*#__PURE__*/React.createElement(Box, {
    className: classes.field
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    className: classes.fieldLabel
  }, "Discount"), /*#__PURE__*/React.createElement(Box, {
    className: classes.discountRow
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.discountType
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    id: "coupon-discount-type",
    labelId: "coupon-discount-type-label",
    label: "Type",
    displayEmpty: true,
    value: form.discountType,
    onChange: e => setField('discountType', e.target.value),
    fullWidth: true
  }, COUPON_DISCOUNT_TYPE_OPTIONS.map(opt => /*#__PURE__*/React.createElement(MenuItem, {
    key: opt.value,
    value: opt.value,
    disabled: !!opt.comingSoon
    // The app theme hides a disabled menu item outright (display: none). This one has
    // to stay on screen so Ops can see the type exists and is coming, so the rule is
    // undone for this item only.
    ,
    sx: opt.comingSoon ? {
      '&.Mui-disabled': {
        display: 'flex',
        opacity: 0.5
      }
    } : undefined
  }, opt.label, opt.comingSoon && /*#__PURE__*/React.createElement(Chip, {
    label: "Coming soon",
    size: "small",
    className: classes.comingSoonChip
  }))))), /*#__PURE__*/React.createElement(TextField, {
    className: classes.discountValue,
    placeholder: isPercent ? '30' : '250.00',
    value: form.discountValue,
    onChange: e => setField('discountValue', isPercent ? sanitizeIntegerInput(e.target.value) : sanitizeDecimalInput(e.target.value)),
    error: !!errors.discountValue,
    InputProps: isPercent ? {
      endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "end"
      }, "%")
    } : {
      startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "start"
      }, "$")
    }
  })), errors.discountValue && /*#__PURE__*/React.createElement(Typography, {
    className: classes.errorText
  }, errors.discountValue)), /*#__PURE__*/React.createElement(Box, {
    className: classes.field
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    className: classes.fieldLabel
  }, "Duration"), /*#__PURE__*/React.createElement(SelectMenu, {
    id: "coupon-duration",
    labelId: "coupon-duration-label",
    value: form.duration,
    onChange: e => setField('duration', e.target.value),
    fullWidth: true
  }, COUPON_DURATION_OPTIONS.map(opt => /*#__PURE__*/React.createElement(MenuItem, {
    key: opt.value,
    value: opt.value
  }, opt.label))), /*#__PURE__*/React.createElement(Typography, {
    className: classes.helperText
  }, "How many invoices in a row the discount applies to.")), /*#__PURE__*/React.createElement(Box, {
    className: classes.field
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    className: classes.fieldLabel
  }, "Scope"), /*#__PURE__*/React.createElement(SelectMenu, {
    id: "coupon-scope",
    labelId: "coupon-scope-label",
    value: form.scope,
    onChange: e => setField('scope', e.target.value),
    fullWidth: true
  }, SCOPE_OPTIONS.map(opt => /*#__PURE__*/React.createElement(MenuItem, {
    key: opt.value,
    value: opt.value
  }, opt.label)))), /*#__PURE__*/React.createElement(Box, {
    className: classes.field
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    className: classes.fieldLabel
  }, "Applies to"), /*#__PURE__*/React.createElement(SelectMenu, {
    id: "coupon-applies-to",
    labelId: "coupon-applies-to-label",
    value: form.appliesTo,
    onChange: e => setField('appliesTo', e.target.value),
    fullWidth: true
  }, APPLIES_TO_OPTIONS.map(opt => /*#__PURE__*/React.createElement(MenuItem, {
    key: opt.value,
    value: opt.value
  }, opt.label))), isProductScoped ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    className: classes.productsList
  }, productRows.length === 0 ? /*#__PURE__*/React.createElement(Box, {
    className: classes.productsListItem
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "No products available.")) : productRows.map(product => /*#__PURE__*/React.createElement(Box, {
    key: product.id,
    className: classes.productsListItem
  }, /*#__PURE__*/React.createElement(Checkbox, {
    size: "small",
    checked: form.appliesToProductIds.includes(product.id),
    onChange: createProductToggleHandler(product.id)
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: product.isDeleted ? 'text.secondary' : undefined
  }, formatCouponProductLabel(product))))), errors.appliesToProductIds && /*#__PURE__*/React.createElement(Typography, {
    className: classes.errorText
  }, errors.appliesToProductIds)) : /*#__PURE__*/React.createElement(Typography, {
    className: classes.helperText
  }, "Discount the whole invoice, or only selected products \u2014 e.g. free unit subscription while paper statement and invite fees are still charged.")), /*#__PURE__*/React.createElement(Box, {
    className: classes.field
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    className: classes.fieldLabel
  }, "Expiration"), /*#__PURE__*/React.createElement(SelectMenu, {
    id: "coupon-expiration",
    labelId: "coupon-expiration-label",
    value: form.expirationOption,
    onChange: e => setField('expirationOption', e.target.value),
    fullWidth: true
  }, COUPON_EXPIRATION_OPTIONS.map(opt => /*#__PURE__*/React.createElement(MenuItem, {
    key: opt.value,
    value: opt.value
  }, opt.label))), isCustomExpiration && /*#__PURE__*/React.createElement(Box, {
    className: classes.inlineControl
  }, /*#__PURE__*/React.createElement(DatePicker, {
    format: DATE_FORMAT,
    label: "Expiration date",
    value: form.customExpirationDate,
    minDate: earliestExpiration,
    error: !!errors.expirationOption,
    onChange: date => setField('customExpirationDate', date)
  })), errors.expirationOption ? /*#__PURE__*/React.createElement(Typography, {
    className: classes.errorText
  }, errors.expirationOption) : /*#__PURE__*/React.createElement(Typography, {
    className: classes.helperText
  }, resolvedExpiration ? "Expires ".concat(format(resolvedExpiration, EXPIRES_FORMAT)) : 'This code can be redeemed until you deactivate it.')), IS_REDEMPTION_LIMIT_VISIBLE && /*#__PURE__*/React.createElement(Box, {
    className: classes.field
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    className: classes.fieldLabel
  }, "Max redemptions"), /*#__PURE__*/React.createElement(SelectMenu, {
    id: "coupon-max-redemptions",
    labelId: "coupon-max-redemptions-label",
    value: form.maxRedemptionsOption,
    onChange: e => setField('maxRedemptionsOption', e.target.value),
    fullWidth: true
  }, COUPON_MAX_REDEMPTION_OPTIONS.map(opt => /*#__PURE__*/React.createElement(MenuItem, {
    key: opt.value,
    value: opt.value
  }, opt.label))), isCustomMaxRedemptions && /*#__PURE__*/React.createElement(Box, {
    className: classes.inlineControl
  }, /*#__PURE__*/React.createElement(TextField, {
    className: classes.customNumberInput,
    placeholder: "Number of redemptions",
    value: form.customMaxRedemptions,
    onChange: e => setField('customMaxRedemptions', sanitizeIntegerInput(e.target.value)),
    error: !!errors.maxRedemptionsOption
  })), errors.maxRedemptionsOption && /*#__PURE__*/React.createElement(Typography, {
    className: classes.errorText
  }, errors.maxRedemptionsOption)));
});